import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import moment from 'moment';
import { useHistory, useParams } from 'react-router';
import Input from '../../../components/Input';
import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import Loader from '../../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
}));

const PsychiatryEvolutionForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [record, setRecord] = useState<Record | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);
        setRecord(recordResponse?.data);

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });

        const formRequest = {
          type: 'psychiatryEvolution',
          title: 'Evolução Psiquiátrica do Paciente',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        history.push(`/prontuarios/${id}`);
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar evolução.',
          });
        }
      }
    },
    [api, record, history, formId, id, signatures],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              EVOLUÇÃO PSIQUIÁTRICA DO PACIENTE
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={2}>
              <FormLabel className={classes.formTitles}>
                Referente a consulta:
              </FormLabel>
            </Grid>

            <Grid item xs={2}>
              <Input id="period_month" label="Mês" name="period_month" />
            </Grid>

            <Grid item xs={2}>
              <Input id="period_year" label="Ano" name="period_year" />
            </Grid>
          </Grid>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Input
                  id="evolution"
                  label="Evolução"
                  name="evolution"
                  isMultiline
                  rows={10}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2} justify="flex-end">
              <Grid item xs={2}>
                <Input
                  id="form_data"
                  label="Data"
                  name="form_data"
                  defaultValue={moment().format('DD/MM/YYYY').toString()}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push(`/prontuarios/${id}`)}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default PsychiatryEvolutionForm;
